<script>
  const AMOUNT_VALID_RE = /^\d+(\.\d{1,2})?$/

  let {
    amount = $bindable(''),
    selectedPreset = $bindable(null),
    frequency = $bindable('one-time'),
    presetAmounts = [],
    onnext,
  } = $props()

  let errors = $state({})
  let amountEl = $state()
  let mask
  let syncing = false

  const numberFormat = new Intl.NumberFormat('en-US')

  function selectPreset(i) {
    selectedPreset = i
    amount = String(presetAmounts[i])
    if (errors.amount) clearError('amount')
  }

  function getAmountError() {
    if (!amount) return 'Please enter a donation amount.'
    if (!AMOUNT_VALID_RE.test(amount) || parseFloat(amount) < 1) {
      return 'Please enter a valid amount of $1 or more.'
    }
    return null
  }

  function clearError(name) {
    if (!errors[name]) return
    const e = { ...errors }
    delete e[name]
    errors = e
  }

  function setError(name, msg) {
    errors = { ...errors, [name]: msg }
  }

  function onAmountFocus() {
    clearError('amount')
  }

  function onAmountBlur() {
    const msg = getAmountError()
    if (msg) {
      setError('amount', msg)
    } else if (amount.includes('.')) {
      amount = parseFloat(amount).toFixed(2)
    }
  }

  function validate() {
    const e = {}
    const msg = getAmountError()
    if (msg) e.amount = msg
    errors = e
    return !msg
  }

  function handleNext() {
    if (validate()) onnext()
  }

  $effect(() => {
    if (!amountEl) return
    ;(async () => {
      const { default: IMask } = await import('imask/esm/imask')
      await import('imask/esm/masked/number')
      mask = IMask(amountEl, {
        mask: Number,
        scale: 2,
        thousandsSeparator: ',',
        radix: '.',
        mapToRadix: ['.'],
        min: 0,
        normalizeZeros: false,
      })
      mask.unmaskedValue = amount
      mask.on('accept', () => {
        if (syncing) return
        amount = mask.unmaskedValue
        selectedPreset = null
        if (errors.amount) clearError('amount')
      })
    })()
    return () => {
      mask?.destroy()
      mask = undefined
    }
  })

  // push external amount changes (preset clicks, blur padding) into the mask.
  // read `amount` first so it's tracked even while `mask` is still loading.
  $effect(() => {
    const v = amount
    if (mask && mask.unmaskedValue !== v) {
      syncing = true
      mask.unmaskedValue = v
      syncing = false
    }
  })
</script>

<style>
  @reference "@/styles/index.css";

  input.has-error {
    @apply outline outline-error;
  }
</style>

<div>
  <h2 class="font-bold uppercase text-primary text-h4 em-secondary">
    <span>Online</span> Donation
  </h2>

  <p class="mt-1 text-sm opacity-50 xl:text-base">( * ) Denotes required field.</p>
</div>

<fieldset class="mt-8">
  <legend class="font-semibold xl:text-xl">Select or enter amount.</legend>

  {#if errors.amount}
    <p id="amount-error" class="form-field-message bad">{errors.amount}</p>
  {/if}

  <ul class="grid flex-wrap grid-cols-2 gap-3 my-5 md:flex">
    {#each presetAmounts as preset, i}
      <li>
        <input
          id={`preset-${i}`}
          type="radio"
          name="preset"
          value={i}
          class="absolute opacity-0 size-0 peer"
          checked={selectedPreset === i}
          onchange={() => selectPreset(i)}
        />
        <label for={`preset-${i}`} class="block py-3 px-4 text-lg font-bold text-center bg-white rounded-lg transition-colors cursor-pointer outline-white md:px-6 xl:text-xl text-primary outline peer-checked:text-white peer-checked:bg-primary hover:outline-primary">
          ${numberFormat.format(preset)}
        </label>
      </li>
    {/each}
  </ul>

  <div class="relative">
    <label for="amount" class="sr-only">Amount</label>
    <div class="grid absolute top-0 left-0 place-content-center h-full w-[3em]" aria-hidden="true">
      <svg fill="none" viewBox="0 0 26 39" class="m-auto w-[1em]">
        <path fill="#0448c5" d="M11.655 38.52v-3.87C4.32 34.2.18 30.6 0 24.525h6.12c.405 3.15 2.07 4.77 5.535 5.13v-8.55c-.09 0-.18 0-.225-.045C5.94 19.8.855 18.315.855 11.88c0-5.805 4.5-8.91 10.8-9.225V0h2.7v2.7c6.03.54 9.855 3.87 10.26 9.54H18.63c-.315-2.52-1.98-4.005-4.275-4.455v7.785c.09.045.18.045.27.045 5.805 1.17 10.755 2.97 10.755 9.36 0 6.03-4.635 9.405-11.025 9.72v3.825zm7.425-12.78c0-2.295-1.755-3.195-4.725-3.96v7.875c2.61-.27 4.725-1.44 4.725-3.915M7.11 11.205c0 2.07 1.44 2.97 4.545 3.78V7.65C8.82 7.92 7.11 9.225 7.11 11.205"/>
      </svg>
    </div>
    <input
      id="amount"
      type="text"
      inputmode="decimal"
      name="amount"
      class="block pr-5 w-full font-bold bg-white rounded-md pl-[2.25em] text-body-md py-[15px] text-primary"
      class:has-error={!!errors.amount}
      aria-invalid={!!errors.amount}
      aria-describedby={errors.amount ? 'amount-error' : undefined}
      bind:this={amountEl}
      onfocus={onAmountFocus}
      onblur={onAmountBlur}
    />
  </div>
</fieldset>

<fieldset class="mt-8">
  <legend class="font-semibold xl:text-xl">Frequency.</legend>

  <div class="grid gap-4 my-5 md:grid-cols-2">
    <div class="relative">
      <input
        id="frequency-one-time"
        type="radio"
        name="frequency"
        value="one-time"
        class="absolute opacity-0 size-0 peer"
        bind:group={frequency}
      />
      <label for="frequency-one-time" class="block relative py-4 px-3 text-lg font-bold text-center bg-white rounded-lg opacity-60 transition-opacity cursor-pointer xl:text-xl hover:opacity-100 text-primary peer-checked:opacity-100 peer-checked:cursor-auto">
        One Time
      </label>

      <div class="grid absolute top-0 right-0 place-content-center h-full transition-transform ease-out scale-0 aspect-square peer-checked:scale-100" aria-hidden="true">
        <svg fill="none" viewBox="0 0 33 33" class="m-auto w-[1.5em]">
          <path fill="#0448c5" d="M0 4.14A4.133 4.133 0 0 1 4.125 0h23.833a4.133 4.133 0 0 1 4.125 4.14v23.92a4.133 4.133 0 0 1-4.125 4.14H4.125A4.13 4.13 0 0 1 0 28.06zm22.471 7.294a1.37 1.37 0 0 0-1.944 0l-6.383 6.406-2.587-2.597a1.37 1.37 0 0 0-1.945 0 1.384 1.384 0 0 0 0 1.951l3.56 3.573a1.37 1.37 0 0 0 1.944 0l7.355-7.382a1.383 1.383 0 0 0 0-1.951"/>
        </svg>
      </div>
    </div>

    <div class="relative">
      <input
        id="frequency-recurring"
        type="radio"
        name="frequency"
        value="recurring"
        class="absolute opacity-0 size-0 peer"
        bind:group={frequency}
      />
      <label for="frequency-recurring" class="block relative py-4 px-3 text-lg font-bold text-center bg-white rounded-lg opacity-60 transition-opacity cursor-pointer xl:text-xl hover:opacity-100 text-primary peer-checked:opacity-100">
        Recurring
      </label>

      <div class="grid absolute top-0 right-0 place-content-center h-full transition-transform ease-out scale-0 aspect-square peer-checked:scale-100" aria-hidden="true">
        <svg fill="none" viewBox="0 0 33 33" class="m-auto w-[1.5em]">
          <path fill="#0448c5" d="M0 4.14A4.133 4.133 0 0 1 4.125 0h23.833a4.133 4.133 0 0 1 4.125 4.14v23.92a4.133 4.133 0 0 1-4.125 4.14H4.125A4.13 4.13 0 0 1 0 28.06zm22.471 7.294a1.37 1.37 0 0 0-1.944 0l-6.383 6.406-2.587-2.597a1.37 1.37 0 0 0-1.945 0 1.384 1.384 0 0 0 0 1.951l3.56 3.573a1.37 1.37 0 0 0 1.944 0l7.355-7.382a1.383 1.383 0 0 0 0-1.951"/>
        </svg>
      </div>
    </div>
  </div>
</fieldset>

<div class="my-8">
  <button type="button" class="w-full btn btn-fill-secondary" onclick={handleNext}>Next</button>
</div>
